import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";

interface Test {
  id: string;
  name: string;
  description: string;
  group: string | null;
  isAutomatable: boolean;
  createdAt: string;
}

interface CreateTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTestCreated: (test: Test) => void;
}

export function CreateTestModal({ isOpen, onClose, onTestCreated }: CreateTestModalProps) {
  const { showError } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    group: "",
    template: {
      testId: "",
      includeExpectations: false
    }
  });
  const [availableTests, setAvailableTests] = useState<Test[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Load available tests for template selection
  useEffect(() => {
    if (isOpen) {
      loadAvailableTests();
    }
  }, [isOpen]);

  const loadAvailableTests = async () => {
    try {
      const response = await api.get<Test[]>("/test");
      const tests = Array.isArray(response) ? response : [];
      setAvailableTests(tests);
    } catch (error) {
      console.error("Failed to load tests:", error);
      setAvailableTests([]);
    }
  };

  // Get unique groups from available tests
  const getGroupOptions = () => {
    const groups = new Set<string>();
    availableTests.forEach(test => {
      if (test.group?.trim()) {
        groups.add(test.group.trim());
      }
    });
    return Array.from(groups).sort().map(group => ({
      value: group,
      label: group
    }));
  };

  // Get template options
  const getTemplateOptions = () => {
    return [
      { value: "", label: "None" },
      ...availableTests.map(test => ({
        value: test.id,
        label: test.name,
        description: test.description
      }))
    ];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showError("Validation Error", "Test name is required");
      return;
    }

    try {
      setIsCreating(true);
      
      const testData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        group: formData.group.trim() || null,
        template: formData.template
      };

      const newTest = await api.post<Test>("/test", testData);
      
      // Reset form
      resetForm();
      
      onTestCreated(newTest);
    } catch (error) {
      console.error("Failed to create test:", error);
      showError("Failed to create test", "Could not create the test. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      group: "",
      template: {
        testId: "",
        includeExpectations: false
      }
    });
  };

  const handleClose = () => {
    if (!isCreating) {
      resetForm();
      onClose();
    }
  };

  const handleTemplateChange = (testId: string) => {
    setFormData(prev => ({ 
      ...prev, 
      template: { 
        ...prev.template, 
        testId,
        // Reset includeExpectations when changing template
        includeExpectations: testId ? prev.template.includeExpectations : false
      } 
    }));
  };

  const handleGroupChange = (group: string) => {
    setFormData(prev => ({ ...prev, group }));
  };

  const handleIncludeExpectationsChange = (checked: boolean) => {
    setFormData(prev => ({ 
      ...prev, 
      template: { 
        ...prev.template, 
        includeExpectations: checked 
      } 
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Create New Test</h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={isCreating}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Test Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Test Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              placeholder="Enter test name"
              disabled={isCreating}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              placeholder="Describe what this test does..."
              rows={3}
              disabled={isCreating}
            />
          </div>

          {/* Group */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Group
            </label>
            <Combobox
              value={formData.group}
              onChange={handleGroupChange}
              options={getGroupOptions()}
              placeholder="Enter or select group"
              allowFreeText={true}
              disabled={isCreating}
            />
          </div>

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Template <span className="text-muted-foreground text-xs">(Optional)</span>
            </label>
            <Combobox
              value={formData.template.testId}
              onChange={handleTemplateChange}
              options={getTemplateOptions()}
              placeholder="Select Template"
              searchable={true}
              disabled={isCreating}
            />
            
            {/* Include Expectations Checkbox */}
            {formData.template.testId && (
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeExpectations"
                  checked={formData.template.includeExpectations}
                  onChange={(e) => handleIncludeExpectationsChange(e.target.checked)}
                  className="w-4 h-4 rounded border-border focus:ring-2 focus:ring-primary text-primary"
                  disabled={isCreating}
                />
                <label htmlFor="includeExpectations" className="text-sm font-medium cursor-pointer">
                  Include expectations
                </label>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isCreating}
              size="sm"
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isCreating || !formData.name.trim()}
              size="sm"
              className="text-xs"
            >
              {isCreating ? "Creating..." : "Create Test"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}